import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import React from "react";
import Item from "../components/Item";
import { usePagesTranslation } from "../locales";
import LegalCompanyIdentity from "./LegalCompanyIdentity";

const USE_LICENSE_ITEMS = [
    "Modify or copy the core application software or underlying source code;",
    "Use the materials for any commercial purpose or public display, except as permitted under the Commercial Teaching License;",
    "Attempt to decompile or reverse engineer any software contained within the application; or",
    "Remove or obscure any copyright, watermark, or other proprietary notations from exported materials.",
];

export default function TermsOfService() {
    const { t } = usePagesTranslation();
    const lastUpdated = process.env.REACT_APP_TERMS_OF_SERVICE_LAST_UPDATED || new Date().toLocaleDateString();
    const translatedItems = t("core.terms.s2Items");
    const useLicenseItems = Array.isArray(translatedItems) ? translatedItems : USE_LICENSE_ITEMS;

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                px: { xs: 1.5, sm: 3, md: 6 },
                pt: { xs: 1, sm: 3, md: 6 },
                pb: { xs: 2, sm: 4, md: 6 },
            }}
        >
            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <Item>
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3, textAlign: 'left' }}>
                            {t("core.terms.title")}
                        </Typography>
                        
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.terms.lastUpdated", { date: lastUpdated })}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left', color: 'text.secondary' }}>
                            {t("core.terms.entityNameNotice")}
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.terms.s1Title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.terms.s1Body")}
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.terms.s2Title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t(
                                "core.terms.s2Lead",
                                "Permission is granted to temporarily use this application for personal, non-commercial transitory viewing only, except as expressly permitted under the Commercial Teaching License granted to active Tutor Standard subscribers. This is the grant of a license, not a transfer of title. Under this license, you may not:",
                            )}
                        </Typography>
                        <Box component="ul" sx={{ m: 0, mb: 2, pl: 3, textAlign: "left" }}>
                            {useLicenseItems.map((item) => (
                                <Typography key={item} component="li" variant="body1" sx={{ mb: 0.75 }}>
                                    {item}
                                </Typography>
                            ))}
                        </Box>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.terms.commercialLicenseTitle", "Commercial Teaching License")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t(
                                "core.terms.commercialLicenseBody",
                                "The Commercial Teaching License grants active Tutor Standard subscribers (including users with active Top-Up PDF Credits) the non-exclusive, non-transferable right to generate, print, and distribute worksheets directly to their own students in private tutoring, homeschool co-ops, or classroom settings.",
                            )}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t(
                                "core.terms.commercialLicenseRestrict",
                                "Reselling, republishing, licensing, or redistributing generated PDF files or puzzle content as standalone digital assets on third-party platforms or marketplaces (including, but not limited to, Etsy, Teachers Pay Teachers, or personal digital storefronts) is strictly prohibited.",
                            )}
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.terms.s3Title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.terms.s3Body")}
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.terms.s4Title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: "left" }}>
                            {t("core.terms.s4Important")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.terms.s4Body1")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.terms.s4Body2")}
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.terms.s5Title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.terms.s5Body")}
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.terms.s6Title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.terms.s6Body")}
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.terms.s7Title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.terms.s7Body")}
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.terms.s8Title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.terms.s8Body")}
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.terms.s9Title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.terms.s9Body")}
                        </Typography>
                        <LegalCompanyIdentity />
                    </Item>
                </Grid>
            </Grid>
        </Box>
    );
}

